import type { ColourAnswers, PreviewCopy } from "../domain.js";

export type EntryRoute = "colour";
export type ColourPath = "quick" | "guided";
export type ViewingContext = "projector" | "laptop" | "phone" | "mixedUnknown";
export type SlideDensity = "sparse" | "moderate" | "dense" | "varied";

export type SharedBrief = {
  route: EntryRoute;
  colourPath?: ColourPath;
  viewingContexts?: ViewingContext[];
  density?: SlideDensity;
  sourceRelationship?: ColourAnswers["sourceRelationship"];
  baseMode?: ColourAnswers["baseMode"];
  dataNeed?: ColourAnswers["dataNeed"];
  dataCount?: number;
  divergingMidpoint?: string;
  colourDeliveryOverride?: ColourAnswers["delivery"];
  previewCopy?: PreviewCopy;
};

const mapViewingToDelivery = (brief: SharedBrief): NonNullable<ColourAnswers["delivery"]> => {
  if (brief.colourDeliveryOverride) return brief.colourDeliveryOverride;
  const contexts = brief.viewingContexts ?? [];
  if (contexts.includes("mixedUnknown") || contexts.length > 1) return "mixed";
  if (contexts.includes("phone")) return "phone";
  if (contexts.includes("projector")) return "live_room";
  if (contexts.includes("laptop")) return "screen_share";
  return "unknown";
};

const mapDensityToLoad = (density: SlideDensity | undefined): NonNullable<ColourAnswers["contentLoad"]> => {
  if (density === "sparse") return "spare";
  if (density === "moderate") return "balanced";
  if (density === "dense") return "dense";
  return "unknown";
};

export const splitSharedBrief = (brief: SharedBrief): { colour: ColourAnswers } => ({
  colour: {
    delivery: mapViewingToDelivery(brief),
    contentLoad: mapDensityToLoad(brief.density),
    sourceRelationship: brief.sourceRelationship ?? "unknown",
    baseMode: brief.baseMode ?? "decide",
    dataNeed: brief.dataNeed ?? "unknown",
    ...(brief.dataCount === undefined ? {} : { dataCount: brief.dataCount }),
    ...(brief.divergingMidpoint?.trim() ? { divergingMidpoint: brief.divergingMidpoint.trim() } : {}),
  },
});

export const pendingQuestionIds = (brief: SharedBrief): string[] => {
  if (brief.colourPath !== "guided") return [];
  const pending: string[] = [];
  if (!brief.viewingContexts || brief.viewingContexts.length === 0) pending.push("viewingContexts");
  if (!brief.density) pending.push("density");
  if (!brief.sourceRelationship) pending.push("sourceRelationship");
  if (!brief.baseMode) pending.push("baseMode");
  if (!brief.dataNeed) pending.push("dataNeed");
  if (["categorical", "sequential", "diverging"].includes(brief.dataNeed ?? "") && brief.dataCount === undefined) pending.push("dataCount");
  if (brief.dataNeed === "diverging" && !brief.divergingMidpoint?.trim()) pending.push("divergingMidpoint");
  return pending;
};
